import Reveal from '@/components/base/Reveal';
import { palette } from '@/mocks/brand';

const contrastPairs = [
  { label: 'Blanc sur marine', ratio: '14,2 : 1', pass: 'AAA', bg: 'bg-primary-500', fg: 'text-background-50' },
  { label: 'Anthracite sur orange', ratio: '6,1 : 1', pass: 'AA', bg: 'bg-accent-500', fg: 'text-foreground-950' },
  { label: 'Marine sur blanc', ratio: '13,4 : 1', pass: 'AAA', bg: 'bg-background-50', fg: 'text-primary-500' },
  { label: 'Gris sur anthracite', ratio: '5,4 : 1', pass: 'AA', bg: 'bg-foreground-950', fg: 'text-secondary-300' },
];

export default function ColorSystem() {
  return (
    <section id="couleurs" className="scroll-mt-32 border-t border-background-200 bg-background-50 py-16 md:py-24">
      <div className="container-wide">
        <Reveal>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-accent-500" />
                <span className="label-mono text-primary-600">02 — La palette</span>
              </div>
              <h2 className="display-title mt-5 text-4xl text-foreground-950 md:text-5xl">
                Cinq couleurs, aucune de trop
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                Le bleu marine tient la structure, l’orange ne sert qu’à désigner l’action ou
                l’information critique, le gris métallique accompagne. Toute autre teinte est
                exclue des supports de marque.
              </p>
            </div>
            <div className="flex flex-none items-center gap-4">
              <div className="h-10 w-10 rounded-md bg-primary-500" />
              <div className="h-10 w-10 rounded-md bg-accent-500" />
              <div className="h-10 w-10 rounded-md bg-secondary-500" />
              <div className="h-10 w-10 rounded-md border border-background-300 bg-background-50" />
              <div className="h-10 w-10 rounded-md bg-foreground-950" />
            </div>
          </div>
        </Reveal>

        <div className="mt-10 overflow-hidden rounded-lg border border-background-200">
          {palette.map((color, index) => (
            <Reveal key={color.hex} delay={index * 60}>
              <div className="grid grid-cols-1 items-stretch gap-0 border-b border-background-200 last:border-b-0 sm:grid-cols-[140px_1fr_auto] md:grid-cols-[180px_1fr_1fr_auto]">
                <div className={`flex min-h-[76px] items-end p-3 ${color.dotClass}`}>
                  <span className={`font-label text-[10px] font-semibold tracking-[0.18em] ${color.textClass}`}>
                    {color.hex}
                  </span>
                </div>
                <div className="px-4 py-3 md:py-4">
                  <p className="font-heading text-lg font-semibold uppercase leading-tight text-foreground-950">
                    {color.name}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-600">{color.role}</p>
                </div>
                <div className="hidden px-4 py-4 md:block">
                  <p className="label-mono text-foreground-400">Usage</p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-700">{color.usage}</p>
                </div>
                <div className="flex items-center gap-2 px-4 pb-3 sm:justify-end sm:pb-0 sm:pr-5 md:pr-5">
                  <span className="font-heading text-2xl font-semibold leading-none text-foreground-950">
                    {color.share}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.14em] text-foreground-400">
                    du visible
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1fr]">
          <Reveal>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <p className="label-mono text-primary-600">Répartition recommandée</p>
              <div className="mt-4 flex h-4 w-full overflow-hidden rounded-full">
                <div className="h-full w-[55%] bg-primary-500" />
                <div className="h-full w-[15%] bg-accent-500" />
                <div className="h-full w-[10%] bg-secondary-500" />
                <div className="h-full w-[15%] border-y border-background-300 bg-background-50" />
                <div className="h-full w-[5%] bg-foreground-950" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-foreground-600 sm:grid-cols-3">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-primary-500" /> Marine 55 %
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-accent-500" /> Orange 15 %
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-secondary-500" /> Métal 10 %
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 border border-background-300 bg-background-50" /> Blanc 15 %
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-foreground-950" /> Anthracite 5 %
                </span>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-foreground-600">
                La surface marine porte la marque, l’orange reste un signal : un seul geste orange
                par écran ou par document suffit.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <p className="label-mono text-primary-600">Contrastes vérifiés</p>
              <div className="mt-4 space-y-2">
                {contrastPairs.map((pair) => (
                  <div
                    key={pair.label}
                    className={`flex items-center justify-between rounded-md px-4 py-3 ${pair.bg}`}
                  >
                    <span className={`text-xs font-semibold ${pair.fg}`}>{pair.label}</span>
                    <span className={`font-label text-[10px] tracking-[0.14em] ${pair.fg} opacity-80`}>
                      {pair.ratio} · {pair.pass}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs leading-relaxed text-foreground-600">
                Aucun texte orange sur blanc pour du texte courant : l’orange est un aplat ou un
                repère, jamais une couleur de lecture.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}